import { DEFAULT_ADDRESS_COUNTRY } from "./addressLocationOptions";

/** Normalize address fields for display / forms (order-only, not profile id). */
export function normalizeAddressFields(src = {}) {
  return {
    street_address: src.street_address || "",
    city: src.city || "",
    state: src.state || "",
    country: src.country || DEFAULT_ADDRESS_COUNTRY,
    zip_code: src.zip_code != null ? String(src.zip_code) : "",
    address_type: src.address_type || "home",
  };
}

function addressHasDisplayContent(addr) {
  if (!addr) return false;
  const normalized = normalizeAddressFields(addr);
  return Boolean(
    normalized.street_address || normalized.city || normalized.country
  );
}

/** Default profile address for an order's customer (API may nest under user objects). */
export function pickProfileDeliveryAddress(addresses) {
  if (!Array.isArray(addresses) || !addresses.length) return null;
  const picked = addresses.find((a) => a?.is_default) || addresses[0];
  return addressHasDisplayContent(picked) ? normalizeAddressFields(picked) : null;
}

/** Copy profile or order address into editable order delivery snapshot. */
export function cloneAddressSnapshot(src) {
  return normalizeAddressFields(src);
}

/**
 * Read delivery address stored on an order (supports common API shapes).
 */
export function getOrderDeliveryAddress(order) {
  if (!order) return null;
  const nested =
    order.delivery_address ||
    order.shipping_address ||
    order.order_address ||
    order.address;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const normalized = normalizeAddressFields(nested);
    if (addressHasDisplayContent(normalized)) {
      return normalized;
    }
  }
  const fromProfile =
    pickProfileDeliveryAddress(order.create_by_user?.addresses) ||
    pickProfileDeliveryAddress(order.user?.addresses);
  if (fromProfile) return fromProfile;
  if (order.street_address || order.city || order.country) {
    return normalizeAddressFields(order);
  }
  return null;
}

/** Multi-line address lines (street, city, country — no address type label). */
export function formatAddressLines(addr) {
  if (!addr) return [];
  const normalized = normalizeAddressFields(addr);
  const lines = [];

  if (normalized.street_address) {
    lines.push(normalized.street_address);
  }

  const city = normalized.city;
  const state = normalized.state;
  const zip = normalized.zip_code;
  const cityParts = [];
  if (city) cityParts.push(city);
  if (state && state.toLowerCase() !== city.toLowerCase()) {
    cityParts.push(state);
  }
  let cityLine = cityParts.join(", ");
  if (zip) {
    cityLine = cityLine ? `${cityLine} - ${zip}` : zip;
  }
  if (cityLine) lines.push(cityLine);

  if (normalized.country) lines.push(normalized.country);
  return lines;
}

export function formatAddressLine(addr) {
  return formatAddressLines(addr).join(", ");
}

/** Payload for POST /order or PATCH /order/:id — does not update profile addresses. */
export function buildOrderAddressApiPayload(values) {
  const delivery_address = normalizeAddressFields(values);
  return { delivery_address };
}
